import React from 'react';

/**
 * Simple markdown renderer for text content
 * Supports: **bold**, *italic*, `code`, lists, and line breaks
 * 
 * @param {string} text - Markdown text to render
 * @returns {React.ReactNode[]} - Array of React elements
 */
export function renderMarkdown(text) {
  if (!text) return null;
  
  // Split by lines to handle lists properly
  const lines = text.split('\n');
  
  return lines.map((line, lineIndex) => {
    // Skip empty or whitespace-only lines
    if (!line.trim()) {
      return <br key={`line-${lineIndex}`} />;
    }
    
    // Check if it's a list item
    const listMatch = line.match(/^[\s]*[-*]\s+(.+)$/);
    const numberedListMatch = line.match(/^[\s]*(\d+)[.)]\s+(.+)$/);
    
    let content = line;
    let isListItem = false;
    let isNumberedList = false;
    let listNumber = '';
    
    if (listMatch) {
      content = listMatch[1];
      isListItem = true;
    } else if (numberedListMatch) {
      listNumber = numberedListMatch[1];
      content = numberedListMatch[2];
      isNumberedList = true;
    }
    
    // Process inline formatting
    const parts = [];
    let currentText = content;
    let key = 0;
    
    // Process **bold**, *italic*, and `code`
    const formatRegex = /(\*\*[^*]+\*\*|\*[^*]+\*|`[^`]+`)/g;
    let match;
    let lastIndex = 0;
    
    while ((match = formatRegex.exec(currentText)) !== null) {
      // Add text before the match
      if (match.index > lastIndex) {
        parts.push(
          <span key={`text-${lineIndex}-${key++}`}>
            {currentText.substring(lastIndex, match.index)}
          </span>
        );
      }
      
      const matchText = match[0];
      
      // Bold
      if (matchText.startsWith('**') && matchText.endsWith('**')) {
        parts.push(
          <strong key={`bold-${lineIndex}-${key++}`}>
            {matchText.slice(2, -2)}
          </strong>
        );
      }
      // Italic
      else if (matchText.startsWith('*') && matchText.endsWith('*')) {
        parts.push(
          <em key={`italic-${lineIndex}-${key++}`}>
            {matchText.slice(1, -1)}
          </em>
        );
      }
      // Code
      else if (matchText.startsWith('`') && matchText.endsWith('`')) {
        parts.push(
          <code 
            key={`code-${lineIndex}-${key++}`}
            style={{
              background: 'rgba(0,0,0,0.1)',
              padding: '2px 6px',
              borderRadius: '4px',
              fontFamily: 'monospace',
              fontSize: '0.9em'
            }}
          >
            {matchText.slice(1, -1)}
          </code>
        );
      }
      
      lastIndex = match.index + matchText.length;
    }
    
    // Add remaining text
    if (lastIndex < currentText.length) {
      parts.push(
        <span key={`text-${lineIndex}-${key++}`}>
          {currentText.substring(lastIndex)}
        </span>
      );
    }
    
    // If no formatting found, just use the content directly
    if (parts.length === 0) {
      parts.push(<span key={`text-${lineIndex}-0`}>{content}</span>);
    }
    
    // Wrap in appropriate element
    if (isListItem) {
      return (
        <div key={`line-${lineIndex}`} style={{ 
          display: 'flex', 
          gap: '8px',
          marginBottom: '8px',
          paddingLeft: '0px',
          alignItems: 'flex-start'
        }}>
          <span style={{ minWidth: '20px' }}>•</span>
          <span style={{ flex: 1 }}>{parts}</span>
        </div>
      );
    } else if (isNumberedList) {
      return (
        <div key={`line-${lineIndex}`} style={{ 
          display: 'flex', 
          gap: '8px',
          marginBottom: '8px',
          paddingLeft: '0px',
          alignItems: 'flex-start'
        }}>
          <span style={{ minWidth: '20px', fontWeight: '600' }}>{listNumber}.</span>
          <span style={{ flex: 1 }}>{parts}</span>
        </div>
      );
    } else if (line.trim() === '') {
      return <br key={`line-${lineIndex}`} />;
    } else {
      return (
        <div key={`line-${lineIndex}`} style={{ marginBottom: '4px' }}>
          {parts}
        </div>
      );
    }
  });
}
